import { type GenericPolyElement, type PointBezierHandle } from '..';
import { reversionElement } from '../../../lib/element';
import { distance, type Point } from '../../../utils/math';
import { type GenericFreedrawElement } from '../../freedraw';
import { getFreedrawShape } from '../../freedraw/utils/shape';

import { getPolyShape } from './shape';

export const isPointRounded = (
  point: Point,
  pointHandles: PointBezierHandle,
  threshold = 10,
) => {
  const allBelowThreshold =
    distance(point[0], pointHandles.before[0]) < threshold &&
    distance(point[1], pointHandles.before[1]) < threshold &&
    distance(point[0], pointHandles.after[0]) < threshold &&
    distance(point[1], pointHandles.after[1]) < threshold;

  return !allBelowThreshold;
};

export const snapToPolyAngle = (
  pos: { x: number; y: number },
  lastPointAbsX: number,
  lastPointAbsY: number,
  points: Point[],
): { x: number; y: number } => {
  const dx = pos.x - lastPointAbsX;
  const dy = pos.y - lastPointAbsY;
  const currentAngle = Math.atan2(dy, dx);

  const allSnapAngles = [];

  // default angles in 45° interval
  for (let i = 0; i < 8; i++) {
    allSnapAngles.push(i * (Math.PI / 4));
  }

  // snap to angles of last n edges (also in 45° interval)
  for (let i = 1; i <= 2; i++) {
    if (points.length >= i + 1) {
      const p_end = points.at(-i)!;
      const p_start = points.at(-(i + 1))!;

      const dx = p_end[0] - p_start[0];
      const dy = p_end[1] - p_start[1];
      const angle = Math.atan2(dy, dx);

      for (let j = 0; j < 8; j++) {
        allSnapAngles.push(angle + j * (Math.PI / 4));
      }
    }
  }

  const uniqueSnapAngles = [
    ...new Set(allSnapAngles.map((angle) => Number(angle.toFixed(6)))),
  ];

  let closestSnapAngle = uniqueSnapAngles[0];
  let minAngleDiff = Infinity;

  for (const snapAngle of uniqueSnapAngles) {
    let diff = currentAngle - snapAngle;

    while (diff <= -Math.PI) diff += 2 * Math.PI;
    while (diff > Math.PI) diff -= 2 * Math.PI;

    if (Math.abs(diff) < minAngleDiff) {
      minAngleDiff = Math.abs(diff);
      closestSnapAngle = snapAngle;
    }
  }

  const snappedAngle = closestSnapAngle;
  const dist = Math.sqrt(dx * dx + dy * dy);

  return {
    x: lastPointAbsX + dist * Math.cos(snappedAngle),
    y: lastPointAbsY + dist * Math.sin(snappedAngle),
  };
};

export const deletePolyPoint = <
  T extends GenericPolyElement | GenericFreedrawElement,
>(
  element: T,
  pointIndex: number,
): T => {
  const newPoints = [...element.points];
  newPoints.splice(pointIndex, 1);
  const newBezierHandles = element.bezierHandles
    ? [...element.bezierHandles]
    : undefined;
  if (newBezierHandles) {
    newBezierHandles.splice(pointIndex, 1);
  }

  const newElement = {
    ...element,
    points: newPoints,
    bezierHandles: newBezierHandles,
  } as T;

  switch (newElement.type) {
    case 'freedraw':
    case 'eraser':
      newElement.shape = getFreedrawShape(newElement as GenericFreedrawElement);
      break;
    case 'poly':
    default:
      newElement.shape = getPolyShape(newElement);
      break;
  }

  newElement.version = {
    ...newElement.version,
    ...reversionElement(element, ['bounds', 'canvas', 'path']).version,
  };

  return newElement;
};
